import React, { useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import { isMapAlive } from './mapGuard';
import type { FeatureCollection } from 'geojson';
import type { LayerId, LayerVisibility } from '../../../types/mapEngine';

interface Props {
  map: mapboxgl.Map;
  taxiRiskData: FeatureCollection | null;
  taxiSpeedData: FeatureCollection | null;
  ciGraphData: FeatureCollection | null;
  tcfGraphData: FeatureCollection | null;
  mhtGraphData: FeatureCollection | null;
  layerVisibility: LayerVisibility;
}

const EMPTY_FC: FeatureCollection = { type: 'FeatureCollection', features: [] };

// ---------------------------------------------------------------------------
// Color ramp expressions
// ---------------------------------------------------------------------------

const TCF_COLOR_RAMP: mapboxgl.Expression = [
  'interpolate',
  ['linear'],
  ['get', 'tcf'],
  0,   '#1565C0',
  0.3, '#388E3C',
  0.6, '#F9A825',
  0.8, '#E64A19',
  1,   '#B71C1C',
];

const MHT_COLOR_RAMP: mapboxgl.Expression = [
  'interpolate',
  ['linear'],
  ['get', 'mht'],
  0,   '#26C6DA',
  0.4, '#1565C0',
  0.7, '#7B1FA2',
  1,   '#E91E63',
];

const CI_COLOR_RAMP: mapboxgl.Expression = [
  'interpolate',
  ['linear'],
  ['get', 'ci'],
  0,   '#37474F',
  0.3, '#1565C0',
  0.6, '#F9A825',
  1,   '#B71C1C',
];

// ---------------------------------------------------------------------------
// Visibility mapping — only the LayerIds this component owns
// ---------------------------------------------------------------------------

const VISIBILITY_MAP: Partial<Record<LayerId, string[]>> = {
  'heatmap-taxi-risk':  ['me-taxi-risk-line'],
  'heatmap-taxi-speed': ['me-taxi-speed-line'],
  'heatmap-ci-graph':   ['me-ci-points'],
  'heatmap-tcf-graph':  ['me-tcf-edges'],
  'heatmap-mht-graph':  ['me-mht-edges'],
};

const HeatmapLineLayer: React.FC<Props> = ({
  map,
  taxiRiskData,
  taxiSpeedData,
  ciGraphData,
  tcfGraphData,
  mhtGraphData,
  layerVisibility,
}) => {
  // Effect 1: add sources + layers on mount
  useEffect(() => {
    // Sources
    if (!map.getSource('me-taxi-risk')) {
      map.addSource('me-taxi-risk', { type: 'geojson', data: taxiRiskData ?? EMPTY_FC });
    }
    if (!map.getSource('me-taxi-speed')) {
      map.addSource('me-taxi-speed', { type: 'geojson', data: taxiSpeedData ?? EMPTY_FC });
    }
    if (!map.getSource('me-ci-nodes')) {
      map.addSource('me-ci-nodes', { type: 'geojson', data: ciGraphData ?? EMPTY_FC });
    }
    if (!map.getSource('me-tcf-edges')) {
      map.addSource('me-tcf-edges', { type: 'geojson', data: tcfGraphData ?? EMPTY_FC });
    }
    if (!map.getSource('me-mht-edges')) {
      map.addSource('me-mht-edges', { type: 'geojson', data: mhtGraphData ?? EMPTY_FC });
    }

    // Layers
    if (!map.getLayer('me-taxi-risk-line')) {
      map.addLayer({
        id: 'me-taxi-risk-line',
        type: 'line',
        source: 'me-taxi-risk',
        paint: {
          'line-width': [
            'interpolate',
            ['linear'],
            ['get', 'tcf'],
            0, 1.5,
            1, 5,
          ],
          'line-color': TCF_COLOR_RAMP,
        },
      });
    }

    if (!map.getLayer('me-taxi-speed-line')) {
      map.addLayer({
        id: 'me-taxi-speed-line',
        type: 'line',
        source: 'me-taxi-speed',
        paint: {
          'line-width': 2.5,
          'line-color': MHT_COLOR_RAMP,
        },
      });
    }

    if (!map.getLayer('me-ci-points')) {
      map.addLayer({
        id: 'me-ci-points',
        type: 'circle',
        source: 'me-ci-nodes',
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['get', 'ci'],
            0, 3,
            1, 9,
          ],
          'circle-color': CI_COLOR_RAMP,
          'circle-stroke-color': 'rgba(255,255,255,0.3)',
          'circle-stroke-width': 1,
        },
      });
    }

    if (!map.getLayer('me-tcf-edges')) {
      map.addLayer({
        id: 'me-tcf-edges',
        type: 'line',
        source: 'me-tcf-edges',
        paint: {
          'line-width': 2,
          'line-color': TCF_COLOR_RAMP,
        },
      });
    }

    if (!map.getLayer('me-mht-edges')) {
      map.addLayer({
        id: 'me-mht-edges',
        type: 'line',
        source: 'me-mht-edges',
        paint: {
          'line-width': 2,
          'line-color': MHT_COLOR_RAMP,
        },
      });
    }

    return () => {
      if (!isMapAlive(map)) return;
      const layerIds = [
        'me-mht-edges',
        'me-tcf-edges',
        'me-ci-points',
        'me-taxi-speed-line',
        'me-taxi-risk-line',
      ];
      layerIds.forEach((id) => {
        if (map.getLayer(id)) map.removeLayer(id);
      });
      ['me-mht-edges', 'me-tcf-edges', 'me-ci-nodes', 'me-taxi-speed', 'me-taxi-risk'].forEach((id) => {
        if (map.getSource(id)) map.removeSource(id);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Effect 2: sync source data when props change
  useEffect(() => {
    const updates: Array<[string, FeatureCollection | null]> = [
      ['me-taxi-risk',  taxiRiskData],
      ['me-taxi-speed', taxiSpeedData],
      ['me-ci-nodes',   ciGraphData],
      ['me-tcf-edges',  tcfGraphData],
      ['me-mht-edges',  mhtGraphData],
    ];
    updates.forEach(([sourceId, data]) => {
      const src = map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined;
      if (src) src.setData(data ?? EMPTY_FC);
    });
  }, [map, taxiRiskData, taxiSpeedData, ciGraphData, tcfGraphData, mhtGraphData]);

  // Effect 3: visibility
  useEffect(() => {
    (Object.keys(VISIBILITY_MAP) as LayerId[]).forEach((key) => {
      const vis = layerVisibility[key];
      if (vis === undefined) return;
      (VISIBILITY_MAP[key] ?? []).forEach((layerId) => {
        if (map.getLayer(layerId)) {
          map.setLayoutProperty(layerId, 'visibility', vis ? 'visible' : 'none');
        }
      });
    });
  }, [map, layerVisibility]);

  return null;
};

export default HeatmapLineLayer;
